import React, { useState, useEffect } from 'react';
import { useStripeContext } from '../contexts/StripeContext';
import { useAuth } from '../contexts/AuthContext';
import './Pricing.css';

const Pricing = () => {
  const { stripePromise } = useStripeContext();
  const { user } = useAuth();
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(true);
  const [currentSubscription, setCurrentSubscription] = useState(null);

  useEffect(() => {
    fetchPricingPlans();
    if (user) {
      fetchCurrentSubscription();
    }
  }, [user]);

  const fetchPricingPlans = async () => {
    try {
      const response = await fetch('/api/payments/plans');
      const data = await response.json();
      setPlans(data.plans);
    } catch (error) {
      console.error('Error fetching pricing plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentSubscription = async () => {
    try {
      const response = await fetch('/api/payments/subscription', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      setCurrentSubscription(data.subscription);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const handleSubscribe = async (planId) => {
    if (!user) {
      alert('Please log in to subscribe');
      return;
    }

    if (planId === 'free') {
      alert('You are already on the free plan');
      return;
    }

    // Check if Stripe is available
    if (!stripePromise) {
      alert('Payment processing is not available. Please check your configuration.');
      return;
    }

    try {
      const response = await fetch('/api/payments/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ planId })
      });

      const data = await response.json();
      
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('Error creating checkout session. Please try again.');
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription?')) {
      return;
    }

    try {
      const response = await fetch('/api/payments/cancel-subscription', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        alert('Subscription canceled successfully');
        fetchCurrentSubscription();
      } else {
        throw new Error('Failed to cancel subscription');
      }
    } catch (error) {
      console.error('Error canceling subscription:', error);
      alert('Error canceling subscription. Please try again.');
    }
  };

  if (loading) {
    return <div className="pricing-loading">Loading pricing plans...</div>;
  }

  const planList = Object.values(plans);

  // Show configuration notice if Stripe is not available
  if (!stripePromise) {
    return (
      <div className="pricing-container">
        <div className="pricing-header">
          <h1>Pricing Plans</h1>
          <p>Payment processing is not configured yet</p>
        </div>
        <div className="configuration-notice">
          <h3>⚠️ Configuration Required</h3>
          <p>To enable payment processing, please:</p>
          <ol>
            <li>Create a <code>.env.local</code> file in the client directory</li>
            <li>Add: <code>REACT_APP_STRIPE_PUBLISHABLE_KEY=pk_live_...</code></li>
            <li>Restart the development server</li>
          </ol>
          <p>For now, you can use the free plan with limited features.</p>
        </div>
        <div className="pricing-grid">
          {planList.map((plan) => (
            <div key={plan.id} className={`pricing-card ${plan.id === 'pro' ? 'featured' : ''}`}>
              <div className="pricing-header">
                <h3>{plan.name}</h3>
                <div className="price">
                  <span className="currency">$</span>
                  <span className="amount">{plan.price / 100}</span>
                  <span className="period">/{plan.interval}</span>
                </div>
              </div>
              <ul className="pricing-features">
                {plan.features.map((feature, index) => (
                  <li key={index}>{feature}</li>
                ))}
              </ul>
              <div className="pricing-actions">
                <button className="pricing-cta disabled">
                  {plan.id === 'free' ? 'Current Plan' : 'Coming Soon'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="pricing-container">
      <div className="pricing-header">
        <h1>Choose Your Plan</h1>
        <p>Start free, scale as you grow</p>
      </div>

      <div className="pricing-grid">
        {planList.map((plan) => (
          <div key={plan.id} className={`pricing-card ${plan.id === 'pro' ? 'featured' : ''}`}>
            {plan.id === 'pro' && <div className="pricing-badge">Most Popular</div>}
            
            <div className="pricing-header">
              <h3>{plan.name}</h3>
              <div className="price">
                <span className="currency">$</span>
                <span className="amount">{plan.price / 100}</span>
                <span className="period">/{plan.interval}</span>
              </div>
            </div>

            <ul className="pricing-features">
              {plan.features.map((feature, index) => (
                <li key={index}>{feature}</li>
              ))}
            </ul>

            <div className="pricing-actions">
              {currentSubscription && currentSubscription.plan_id === plan.id ? (
                <div className="current-plan">
                  <span className="current-plan-text">Current Plan</span>
                  {plan.id !== 'free' && (
                    <button 
                      className="cancel-button"
                      onClick={handleCancelSubscription}
                    >
                      Cancel Subscription
                    </button>
                  )}
                </div>
              ) : (
                <button 
                  className={`pricing-cta ${plan.id === 'free' ? 'disabled' : ''}`}
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={plan.id === 'free'}
                >
                  {plan.id === 'free' ? 'Current Plan' : 
                   plan.id === 'enterprise' ? 'Contact Sales' : 
                   `Start ${plan.name} ${plan.id === 'pro' ? 'Trial' : ''}`}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {currentSubscription && (
        <div className="current-subscription">
          <h3>Current Subscription</h3>
          <p>Plan: {plans[currentSubscription.plan_id]?.name || 'Unknown'}</p>
          <p>Status: {currentSubscription.status}</p>
          {currentSubscription.current_period_end && (
            <p>Renews: {new Date(currentSubscription.current_period_end).toLocaleDateString()}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default Pricing;
